import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  AppShell,
  AnimatedPage,
  type NavSection,
} from '@theonefamily/ui';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

const HubBar = React.lazy(() => import('hubBar/HubBar'));
import {
  LayoutDashboard,
  Car,
  MapPin,
  Wrench,
  Package,
  ShoppingCart,
  ClipboardCheck,
  Fuel,
  Users,
  Route,
  BarChart3,
  Settings,
  Navigation,
  Monitor,
  Brain,
  MapPinned,
  DollarSign,
  Shield,
  FileText,
  CalendarCheck,
  Calculator,
  FlameKindling,
  Bot,
  Database,
  Play,
  Trophy,
  Boxes,
  MessageSquare,
  FileBarChart,
  Clock,
  Wifi,
  ClipboardList,
} from 'lucide-react';

const NAVIGATION: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/' },
      { id: 'command-center', label: 'Command Center', icon: Monitor, href: '/command-center' },
      { id: 'vehicles', label: 'Vehicles', icon: Car, href: '/vehicles' },
      { id: 'gps', label: 'GPS Tracking', icon: MapPin, href: '/gps' },
      { id: 'dispatch', label: 'Dispatch', icon: Navigation, href: '/dispatch' },
      { id: 'maintenance', label: 'Maintenance', icon: Wrench, href: '/maintenance' },
      { id: 'predictive', label: 'Predictive AI', icon: Brain, href: '/predictive' },
      { id: 'inventory', label: 'Inventory', icon: Package, href: '/inventory' },
      { id: 'orders', label: 'Store', icon: ShoppingCart, href: '/orders' },
      { id: 'inspections', label: 'Inspections', icon: ClipboardCheck, href: '/inspections' },
      { id: 'fuel', label: 'Fuel', icon: Fuel, href: '/fuel' },
      { id: 'trips', label: 'Trip Logs', icon: Route, href: '/trips' },
      { id: 'visit-logs', label: 'Client Visits', icon: MapPinned, href: '/visit-logs' },
      { id: 'cost-allocation', label: 'Cost Allocation', icon: DollarSign, href: '/cost-allocation' },
      { id: 'safety', label: 'Safety Scores', icon: Shield, href: '/safety' },
      { id: 'work-orders', label: 'Work Orders', icon: FileText, href: '/work-orders' },
      { id: 'compliance', label: 'Compliance', icon: CalendarCheck, href: '/compliance' },
      { id: 'tco', label: 'TCO Analysis', icon: Calculator, href: '/tco' },
      { id: 'fuel-analytics', label: 'Fuel Analytics', icon: FlameKindling, href: '/fuel-analytics' },
      { id: 'agent-dispatch', label: 'Auto Dispatch', icon: Bot, href: '/agent-dispatch' },
      { id: 'cmdb-sync', label: 'CMDB Sync', icon: Database, href: '/cmdb-sync' },
      { id: 'route-replay', label: 'Route Replay', icon: Play, href: '/route-replay' },
      { id: 'benchmarks', label: 'Benchmarks', icon: Trophy, href: '/benchmarks' },
      { id: 'parts-forecast', label: 'Parts Forecast', icon: Boxes, href: '/parts-forecast' },
      { id: 'fleet-advisor', label: 'THEO Advisor', icon: MessageSquare, href: '/fleet-advisor' },
      { id: 'qbr-reports', label: 'QBR Reports', icon: FileBarChart, href: '/qbr-reports' },
      { id: 'hos', label: 'HOS Tracking', icon: Clock, href: '/hos' },
      { id: 'iot-devices', label: 'IoT Devices', icon: Wifi, href: '/iot-devices' },
      { id: 'inspection-templates', label: 'Inspection Builder', icon: ClipboardList, href: '/inspection-templates' },
      { id: 'drivers', label: 'Drivers', icon: Users, href: '/drivers' },
      { id: 'reports', label: 'Reports', icon: BarChart3, href: '/reports' },
      { id: 'admin', label: 'Admin', icon: Settings, href: '/admin' },
    ],
  },
];

export function AppLayout() {
  const { isOnline } = useNetworkStatus();
  return (
    <>
      {!isOnline && (
        <div className="sticky top-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium">
          You&apos;re offline — changes will sync when reconnected
        </div>
      )}
      <React.Suspense fallback={<div style={{ height: 44 }} />}>
        <HubBar
          currentProduct="fleet"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonebus.app'}
          hubUrl={import.meta.env.VITE_HUB_URL}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'fleet',
            platformName: 'The One Fleet',
          }}
        />
      </React.Suspense>
      <AppShell
        product={{ name: 'The One Fleet', accent: '#10B981' }}
        navigation={NAVIGATION}
        user={{
          name: 'Fleet Admin',
          email: 'admin@fleet.local',
          role: 'Administrator',
        }}
        onSignOut={() => { window.location.href = '/login'; }}
      >
        <AnimatedPage>
          <Outlet />
        </AnimatedPage>
      </AppShell>
    </>
  );
}
